import { Alert, Button, Input } from "@xapp/components";
import React, { FC, FormEvent, useCallback, useState } from "react";

import { MagicLinkSession, requestSignInCode, submitSignInCode } from "auth/utils/magicLink";

import style from "./style.scss";

export interface MagicLinkSignInProps {
    /** Pre-fills the address, e.g. from a form the prospect already filled in. */
    initialEmail?: string;
    className?: string;
    /** Same shape the password flow hands back, so callers treat them alike. */
    onSignIn?(session: MagicLinkSession): void;
}

type Stage = "email" | "code";

/**
 * Sign in with a code emailed to you — no password.
 *
 * Cognito's CUSTOM_AUTH flow. Deliberately self-contained rather than folded
 * into `SignUp`: it shares no state with that form, and `SignUp` is already a
 * ~500-line component doing five things.
 *
 * The two awkward behaviours here are Cognito's, not ours, and both were found
 * by running the flow against dev rather than by reading the docs:
 *
 * A **wrong code does not raise an error**. Cognito re-issues the challenge and
 * allows three attempts, so nothing is thrown and no session appears. Handling
 * only exceptions would leave the button looking dead.
 *
 * The **challenge session expires in about three minutes**, which is shorter
 * than the code's own ten. Someone who reads their email slowly arrives with a
 * perfectly valid code and a dead session, and telling them the code is wrong
 * would be a lie — so that case offers a restart instead.
 */
export const MagicLinkSignIn: FC<MagicLinkSignInProps> = (props) => {
    const { initialEmail = "", className, onSignIn } = props;

    const [stage, setStage] = useState<Stage>("email");
    const [email, setEmail] = useState<string>(initialEmail);
    const [code, setCode] = useState<string>("");
    const [busy, setBusy] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [notice, setNotice] = useState<string>("");

    const restart = useCallback(() => {
        setStage("email");
        setCode("");
        setError("");
        // Also the notice: leaving "Check your email" up while they type a
        // different address makes it read as being about the new one.
        setNotice("");
    }, []);

    const handleSendCode = useCallback(async () => {
        if (busy || !email.trim()) return;
        setBusy(true);
        setError("");
        try {
            const awaitingCode = await requestSignInCode(email);
            if (awaitingCode) {
                setStage("code");
                setNotice("Check your email for a sign-in code.");
            } else {
                // Cognito signed them in without asking. Not expected on this
                // pool, but saying so beats pretending a code is coming.
                setError("Signed in without a code. Try reloading.");
            }
        } catch (e) {
            setError(messageFor(e));
        } finally {
            setBusy(false);
        }
    }, [busy, email]);

    const handleSubmitCode = useCallback(async () => {
        if (busy || !code.trim()) return;
        setBusy(true);
        setError("");
        try {
            const session = await submitSignInCode(code);
            if (!session) {
                // No throw, no session: the code was wrong. See the note above.
                setError("That code didn't match. Check the email and try again.");
                return;
            }
            if (typeof onSignIn === "function") {
                onSignIn(session);
            }
        } catch (e) {
            setError(messageFor(e));
        } finally {
            setBusy(false);
        }
    }, [busy, code, onSignIn]);

    // Enter is how people submit a sign-in field; without a form it does
    // nothing and the screen feels broken before they find the button.
    const onSubmit = useCallback(
        (handler: () => void) => (e: FormEvent) => {
            e.preventDefault();
            handler();
        },
        []
    );

    return (
        <div className={className}>
            {stage === "email" ? (
                <form className={style.magicLinkForm} onSubmit={onSubmit(handleSendCode)}>
                    <Input
                        label="Email"
                        variation="outlined"
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(value: string) => setEmail(value)}
                    />
                    <Button type="submit" variation="primary" loading={busy} disabled={!email.trim()}>
                        Email me a sign-in code
                    </Button>
                </form>
            ) : (
                <form className={style.magicLinkForm} onSubmit={onSubmit(handleSubmitCode)}>
                    <p>{`We emailed a code to ${email}. It's good for 10 minutes.`}</p>
                    <Input
                        label="Code"
                        variation="outlined"
                        // Gets the numeric keypad and OTP autofill on mobile.
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        value={code}
                        onChange={(value: string) => setCode(value)}
                    />
                    <Button type="submit" variation="primary" loading={busy} disabled={!code.trim()}>
                        Sign in
                    </Button>
                    <Button type="button" variation="flat" disabled={busy} onClick={restart}>
                        Use a different email
                    </Button>
                </form>
            )}

            <Alert handleClose={() => setError("")} open={!!error} text={error} severity="error" />
            <Alert handleClose={() => setNotice("")} open={!!notice} text={notice} severity="success" />
        </div>
    );
};

/**
 * Cognito's own wording is fine for most failures, but an expired challenge
 * session reads as "Invalid session for the user" — which sounds like the
 * account is wrong when the only problem is that a few minutes passed.
 */
function messageFor(e: unknown): string {
    const raw = (e as Error)?.message || "Something went wrong. Try again.";
    if (/invalid session/i.test(raw)) {
        return "That took too long — request a new code.";
    }
    return raw;
}
